import React, { useEffect, useState } from 'react'

import {
  ADD,
  AddDocument,
  Button,
  Input,
  Multiselect,
  SubmitSuccess,
  Textarea,
  useNotificationContext,
  LoadedFile,
} from 'shared/components'

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { handleReCaptchaVerify, handleTokenError } from 'shared/utils'
import { PartnershipApi } from 'api'

import { FormTypes } from 'models'
import { GitlabServicesMtsRuMtsMediaCorporateCorporateBackendPartnershipPartnership as FormBody } from 'api/models'

import classNames from 'classnames/bind'
import classes from './Form.module.scss'

export type FormData = {
  [key: string]: string
}

const cx = classNames.bind(classes)

const formsApi = new PartnershipApi()

export type FormFieldType = {
  type: 'input' | 'textarea' | 'file' | 'multiselect'
  required: boolean
  label: string
  description?: string
  placeholder: string
  colClasses: string
  name:
    | 'email'
    | 'name'
    | 'company'
    | 'department'
    | 'files'
    | 'message'
    | 'phone'
    | 'position'
    | 'surname'
  inFirstCol?: boolean
  format?: RegExp
  options?: any[]
  selectedValues?: any[]
  valuePath?: string
  displayValue?: string
  maxSize?: number
  maxLength?: number
  allowFormat?: string[] // in uppercase
  mask?: string
  tooltipText?: string
  validatorFn?: (v: string) => string
}

export type FormProps = {
  type: FormTypes
  fields: FormFieldType[]
  title?: string | JSX.Element
  dark?: boolean
  showUserAgreement?: boolean
  open?: boolean
  onCloseClick?: () => void
  formClasses?: string
  containerClasses?: string
  titleClasses?: string
  id: string
  onAutoClose?: () => void
  showed?: boolean
  isTwoColumns?: boolean
  titleInSecondCol?: boolean
  titleLarge?: boolean
  footerTwoColumns?: boolean
  closeToTop?: boolean
  onSubmit?: () => void
  onSelect?: (v: any) => void
}

export const Form = ({
  title,
  titleInSecondCol,
  id,
  type,
  showUserAgreement,
  fields,
  onCloseClick = () => false,
  onAutoClose,
  showed,
  formClasses = '',
  titleClasses = '',
  containerClasses = '',
  dark,
  isTwoColumns,
  titleLarge,
  closeToTop,
  onSubmit,
  onSelect,
}: FormProps): JSX.Element => {
  const [data, setData] = useState<FormData>({})
  const [files, setFiles] = useState<LoadedFile[]>([])
  const [errors, setErrors] = useState<FormData>({})
  const [isSubmit, setIsSubmit] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isBtnDisabled, setIsBtnDisabled] = useState(false)
  const { notificationsDispatch } = useNotificationContext()
  const _containerClasses = cx('container', {
    dark,
    [containerClasses]: !!containerClasses,
  })

  const { executeRecaptcha } = useGoogleReCaptcha()

  useEffect(() => {
    setSelectedData()
    setErrors({})
    setIsSubmit(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showed])

  const renderTitle = () => {
    const _classes = cx('form__title', {
      [titleClasses]: !!titleClasses,
      form__title_large: titleLarge,
    })

    return <h3 className={_classes}>{title}</h3>
  }

  const setSelectedData = () => {
    const _data: FormData = {}

    for (let i = 0; i < fields.length; i++) {
      const selected = fields[i]?.selectedValues || []
      if (selected.length > 0) {
        _data[fields[i].name] = selected[0]?.[fields[i]?.valuePath || 'id']
      }
    }

    setData(_data)
  }

  const renderFooter = (isCol = true) => {
    const _classes = classNames([classes.form_footer], {
      'col-xs-12': isCol,
      [classes.form_footer_col]: isCol,
    })

    return (
      <div className={_classes}>
        <Button
          disabled={isLoading || isBtnDisabled}
          className={classes.form_button}
          onClick={() => handleSubmit()}
        >
          Отправить
        </Button>

        {showUserAgreement && (
          <small className={classes.form_agreement}>
            Нажимая на кнопку “Отправить”, вы принимаете <br />
            <a
              href={require('assets/docs/Пользовательское соглашение.docx')}
              className="link color"
              target="_blank"
              rel="noreferrer"
            >
              условия пользовательского соглашения
            </a>
          </small>
        )}
      </div>
    )
  }

  const renderInput = (f: FormFieldType) => {
    return (
      <Input
        onClear={() => handleFieldChange(f, '')}
        id={f.name}
        placeholder={f.placeholder}
        label={f.label}
        value={data?.[f.name] || ''}
        onChange={ev => {
          handleFieldChange(f, ev.target.value)
        }}
        desc={errors?.[f.name]}
        error={errors?.[f.name]?.length > 0}
        dark={dark}
        success={!errors?.[f.name]?.length && data?.[f.name]?.length > 0}
        maxLength={f?.maxSize}
        mask={f?.mask}
        disabled={isLoading}
      />
    )
  }

  const renderMultiselect = (f: FormFieldType) => {
    return (
      <Multiselect
        singleSelect
        label={f.label}
        dark={dark}
        options={f?.options}
        valuePath={f?.valuePath}
        displayValue={f?.displayValue}
        selectedValues={f?.selectedValues}
        onSelect={v => {
          const _v = v[0]?.[f?.valuePath || 'id']
          handleFieldChange(f, _v)
          onSelect && onSelect(_v)
        }}
        disable={isLoading}
      />
    )
  }

  const renderTextarea = (f: FormFieldType) => {
    return (
      <Textarea
        className={classes.form_field}
        required={false}
        value={data?.[f.name] || ''}
        placeholder={f.placeholder}
        label={f.label}
        cb={v => {
          handleFieldChange(f, v)
        }}
        isDark={dark}
        desc={errors?.[f.name]}
        error={errors?.[f.name]?.length > 0}
        maxLength={f?.maxSize}
        disabled={isLoading}
      />
    )
  }

  const renderFilesUpload = (f: FormFieldType) => {
    return (
      <>
        <AddDocument
          btnGray={!dark}
          maxFilesLength={f.maxLength}
          maxFileSize={f.maxSize}
          allowFormat={f?.allowFormat}
          onChange={files => {
            setFiles(files)
            files.length && setErrors({ ...errors, files: '' })
          }}
          onFileError={err =>
            err.length &&
            setErrors({ ...errors, files: 'Удалите файлы с ошибками' })
          }
          isShowed={showed}
          setBtnDisabled={v => setIsBtnDisabled(v)}
          disabled={isLoading}
        />
        {f.description && (
          <small title={f.tooltipText} className={cx('descr')}>
            {f.description}
          </small>
        )}
      </>
    )
  }

  const renderField = (f: FormFieldType, i: number, additionalClasses = '') => {
    let field

    switch (f.type) {
      case 'textarea':
        field = renderTextarea(f)
        break
      case 'file':
        field = renderFilesUpload(f)
        break
      case 'multiselect':
        field = renderMultiselect(f)
        break
      default:
        field = renderInput(f)
    }

    return (
      <div key={i} className={`${f.colClasses} ${additionalClasses}`}>
        {field}
      </div>
    )
  }

  const renderFields = () => {
    if (isTwoColumns) {
      const firstCol: JSX.Element[] = []
      const secondCol: JSX.Element[] = []

      for (let i = 0; i < fields.length; i++) {
        const field = renderField(fields[i], i, classes['form_field_max-width'])

        if (fields[i]?.inFirstCol) {
          firstCol.push(field)
        } else {
          secondCol.push(field)
        }
      }

      return (
        <>
          <div className={`col-xs-12 col-lg-7 ${classes.form_first_col}`}>
            {firstCol}
            {renderFooter(false)}
          </div>
          <div className={`col-xs-12 col-lg-5 ${classes.form_second_col}`}>
            {titleInSecondCol && renderTitle()}
            {secondCol}
            {renderFooter(false)}
          </div>
        </>
      )
    }

    return fields.map((f, i) => renderField(f, i))
  }

  const renderClose = () => {
    const closeBtnClasses = cx('button_reset', 'close', {
      top: closeToTop,
    })

    return (
      <button className={closeBtnClasses} onClick={() => onCloseClick()}>
        <i className="icon-close" />
      </button>
    )
  }

  const renderReply = () => {
    return (
      <SubmitSuccess
        isDark={dark}
        customClasses={classes.form__reply}
        onSvgAnimationEnd={() => {
          setIsLoading(false)
          onAutoClose && onAutoClose()
        }}
      />
    )
  }

  const validateField = (f: FormFieldType, v: string) => {
    if (f.type === 'file' && f.required && !files.length) {
      return 'Загрузите файл'
    }
    if (!v?.length && f.required && f.type !== 'file') {
      return 'Введите значение'
    }
    if (f?.validatorFn) {
      return f.validatorFn(v)
    }
    if (f?.maxSize && v?.length > f.maxSize) {
      return `Макс. длина: ${f.maxSize} символов`
    }
    if (f?.format && !f?.format.test(v)) {
      return 'Неверный формат'
    }

    return ''
  }

  const handleFieldChange = (f: FormFieldType, v: string) => {
    const error = validateField(f, v)

    setData({ ...data, [f.name]: v })
    setErrors({ ...errors, [f.name]: error })
  }

  const checkErrors = () => {
    const _errors: FormData = {}

    fields.forEach(f => {
      const error = errors?.[f.name] || validateField(f, data?.[f?.name])

      if (error) {
        _errors[f.name] = error
      }

      if (error && f.type === 'file') {
        notificationsDispatch({
          type: ADD,
          payload: {
            type: 'error',
            description: error,
          },
        })
      }
    })

    return { _errors, _errorsLength: Object.keys(_errors).length }
  }

  const createFormData = () => {
    const d: FormBody = {
      email: data['email'],
      name: data['name'],
      surname: data['surname'],
      company: data['company'],
      message: data['message'],
      department: data['department'],
      phone: data['phone'],
      position: data['position'],
      files: files.length ? files : undefined,
      type,
    }

    return d
  }

  const submit = (token: string) => {
    formsApi
      .savePartnership(createFormData(), token)
      .then(() => {
        setIsSubmit(true)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }

  const handleSubmit = () => {
    const { _errors, _errorsLength } = checkErrors()

    if (_errorsLength) {
      setErrors(_errors)
    } else {
      setIsLoading(true)

      handleReCaptchaVerify(executeRecaptcha)
        .then(token => {
          if (token) {
            submit(token)
            onSubmit && onSubmit()
          } else {
            handleTokenError()
          }
        })
        .catch(() => setIsLoading(false))
    }
  }

  const renderForm = () => {
    const _formClasses = cx('form', {
      hidden: isSubmit,
      [formClasses]: !!formClasses,
    })

    return (
      <form className={_formClasses} onSubmit={ev => ev.preventDefault()}>
        <div className="row">
          {title && !titleInSecondCol && (
            <div className="col-xs-12">{renderTitle()}</div>
          )}

          {renderFields()}

          {!isTwoColumns && renderFooter()}
        </div>
      </form>
    )
  }

  return (
    <div className={_containerClasses} id={id}>
      {!isLoading && !isBtnDisabled && renderClose()}
      {renderForm()}
      {isSubmit && renderReply()}
    </div>
  )
}
